import React, { createContext, useState, useContext, useEffect } from 'react';
import { getStoredToken, getStoredUser, setStoredToken, setStoredUser, clearStoredToken, verifyJWT, createJWT } from '../utils/auth';
import { adminAuthService } from '../services/adminAuth';

const AdminAuthContext = createContext();

export const AdminAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = getStoredToken();
        const storedUser = getStoredUser();

        if (token && storedUser) {
          const decoded = verifyJWT(token);
          if (decoded) {
            setUser(storedUser);
            setUserRoles(storedUser.roles || []);

            try {
              const permsResult = await adminAuthService.fetchUserPermissions(storedUser.id);
              setUserPermissions(permsResult);
            } catch (err) {
              console.warn('Failed to fetch permissions, continuing with cached roles:', err);
              setUserPermissions([]);
            }
          } else {
            clearStoredToken();
          }
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    setError(null);

    try {
      const result = await adminAuthService.login(email, password);

      if (result.success) {
        setUser(result.user);
        setUserRoles(result.user.roles || []);
        setStoredUser(result.user);

        const token = createJWT({ 
          userId: result.user.id, 
          email: email,
          roles: result.user.roles 
        });
        setStoredToken(token);

        try {
          const permsResult = await adminAuthService.fetchUserPermissions(result.user.id);
          setUserPermissions(permsResult);
        } catch (err) {
          console.warn('Failed to fetch permissions:', err);
          setUserPermissions([]);
        }

        setLoading(false);
        return { success: true };
      } else {
        setError(result.error);
        setLoading(false);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMsg = err.message || 'Login failed';
      setError(errorMsg);
      setLoading(false);
      return { success: false, error: errorMsg };
    }
  };

  const logout = () => {
    setUser(null);
    setUserRoles([]);
    setUserPermissions([]);
    clearStoredToken();
  };

  const hasPermission = (permissionName) => {
    if (!user) return false;
    return userPermissions.some(p => p.name === permissionName);
  };

  const hasAnyPermission = (permissionNames) => {
    if (!user || !Array.isArray(permissionNames)) return false;
    return permissionNames.some(perm => userPermissions.some(p => p.name === perm));
  };

  const hasAllPermissions = (permissionNames) => {
    if (!user || !Array.isArray(permissionNames)) return false;
    return permissionNames.every(perm => userPermissions.some(p => p.name === perm));
  };

  const value = {
    user,
    userRoles,
    userPermissions,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!user,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};
